"use client";

import { type TCard } from "@/components";
import { HmcCard } from "@adi-ctx/harmonic-core-components-react";
import type { Hit, BaseHit } from "instantsearch.js";

import { Highlight } from "react-instantsearch";
import {
	RiCalendarEventLine,
	RiLightbulbFlashLine,
	RiPagesLine,
	RiTerminalBoxLine,
} from "@remixicon/react";

type SearchResultCardProps = {
	readonly card: Partial<TCard | undefined>;
	readonly hit: Hit<BaseHit>;
};

enum SearchIcon {
	Event = "event",
	Solution = "solution-center",
	Driver = "driver",
	CustomPage = "custom-page",
}

export default function SearchResultCard({ card, hit }: SearchResultCardProps) {
	const iconMap = new Map();

	iconMap.set(SearchIcon.Solution, {
		icon: <RiLightbulbFlashLine size={16} className="mr-[2px]" />,
		label: "Software Solutions",
	});
	iconMap.set(SearchIcon.Driver, {
		icon: <RiTerminalBoxLine size={16} className="mr-[2px]" />,
		label: "Device Driver",
	});
	iconMap.set(SearchIcon.Event, {
		icon: <RiCalendarEventLine size={16} className="mr-[2px]" />,
		label: "Event",
	});
	iconMap.set(SearchIcon.CustomPage, {
		icon: <RiPagesLine size={16} className="mr-[2px]" />,
		label: "Other",
	});

	if (!card) return;

	return (
		<section className="mb-2 flex flex-col bg-gray-100">
			<HmcCard data-testid="search-result-card">
				<div
					slot="card-header"
					className="flex w-full items-center justify-between pb-3"
				>
					<div className="flex w-full flex-col">
						<h2 className="text-base font-bold">
							<Highlight
								attribute="name"
								hit={hit}
								classNames={{
									highlighted: "bg-blue-100 text-blue-600 font-bold",
								}}
							/>
						</h2>
					</div>

					<div className="flex h-[24px] min-w-fit items-center bg-purple-700 px-1 py-[2px] text-white">
						<span>{iconMap.get(card?.type).icon}</span>
						<span className="text-sm">{iconMap.get(card?.type).label}</span>
					</div>
				</div>

				<div className="flex justify-end gap-2">
					{card?.description && (
						<p className="truncate-2-lines-body-xs hmc-body-xs text-contentSubtle">
							<Highlight
								attribute="description"
								hit={hit}
								classNames={{
									highlighted: "bg-blue-100 text-blue-600 font-bold",
								}}
							/>
						</p>
					)}
				</div>
			</HmcCard>
		</section>
	);
}
